import { useEffect, useState } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { useUserStore } from '@/stores/userStore';
import { useAuthStore } from '@/auth/authStore';
import { authApi } from '@/auth/authApi';
import { Pill } from '@/components/Pill';
import { BackButton } from '@/components/BackButton';
import { CosmeticPreview } from '@/shop/CosmeticPreview';
import { CoinIcon } from '@/components/CurrencyIcon';
import { SkillsShopSection } from '@/shop/SkillsShopSection';
import { CharactersShopSection } from '@/characters/CharactersShopSection';
import { HoldSkinsShopSection } from '@/holdskins/HoldSkinsShopSection';
import { GemsShopSection } from '@/iap/GemsShopSection';
import { audio } from '@/audio/AudioService';
import { useT } from '@/i18n/useI18n';
import type { I18nKey } from '@/i18n/translations';
import { resolveAssetUrl } from '@/shop/assetUrl';
import { PreviewDialog } from '@/shop/PreviewDialog';
import { absoluteAssetUrl, getPreloadedMedia } from '@/net/assetPrefetch';
import { adsAvailable, showRewarded } from '@/ads/adService';
import { trialApi, type TrialEntry, type TrialKind } from '@/shop/trialApi';
import {
  CharacterAnimationPreview,
  type CharacterAnimationSource,
} from '@/characters/CharacterAnimationPreview';
import {
  shopApi,
  ShopApiError,
  RARITY_COLOR,
  type CosmeticItem,
  type ShopBundle,
} from '@/shop/shopApi';

// Top-level shop categories. Exactly one category shows at a time.
type Category = 'combos' | 'heroes' | 'skills' | 'holds' | 'frames' | 'finishers' | 'backgrounds' | 'gems';

interface FinisherPreviewSource {
  id: string;
  name: string;
  iconUrl: string | null;
  data: Record<string, unknown>;
}

const CATEGORIES: Array<{ id: Category; labelKey: I18nKey; icon: string; accent: string }> = [
  { id: 'gems', labelKey: 'shop.cat_gems', icon: '💎', accent: 'var(--c-cyan)' },
  { id: 'combos', labelKey: 'shop.cat_combos', icon: '🎁', accent: 'var(--c-yellow)' },
  { id: 'heroes', labelKey: 'shop.cat_heroes', icon: '🧗', accent: 'var(--c-purple)' },
  { id: 'skills', labelKey: 'shop.cat_skills', icon: '⚡', accent: 'var(--c-cyan)' },
  { id: 'holds', labelKey: 'shop.cat_holds', icon: '🪨', accent: 'var(--c-red)' },
  { id: 'finishers', labelKey: 'shop.cat_finishers', icon: '💥', accent: 'var(--c-purple)' },
  { id: 'frames', labelKey: 'shop.cat_frames', icon: '🖼️', accent: 'var(--c-yellow)' },
  { id: 'backgrounds', labelKey: 'shop.cat_backgrounds', icon: '🌌', accent: 'var(--c-cyan)' },
];

export function ShopScreen() {
  const go = useUIStore((s) => s.go);
  const returnTo = useUIStore((s) => s.returnTo);
  const setReturnTo = useUIStore((s) => s.setReturnTo);
  const profile = useUserStore((s) => s.profile);
  const setProfile = useUserStore((s) => s.setProfile);
  const token = useAuthStore((s) => s.token);
  const setUser = useAuthStore((s) => s.setUser);
  const t = useT();

  const [items, setItems] = useState<CosmeticItem[] | null>(null);
  const [bundles, setBundles] = useState<ShopBundle[] | null>(null);
  const [ownedIds, setOwnedIds] = useState<Set<string>>(new Set());
  const [trialsByItem, setTrialsByItem] = useState<Map<string, TrialEntry>>(new Map());
  const [category, setCategory] = useState<Category>('skills');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null);
  const [previewCharacter, setPreviewCharacter] = useState<CharacterAnimationSource | null>(null);
  const [previewFinisher, setPreviewFinisher] = useState<FinisherPreviewSource | null>(null);
  const [previewBackground, setPreviewBackground] = useState<CosmeticItem | null>(null);

  const load = async () => {
    try {
      const [{ items }, { owned }, { items: bundleItems }, trialResult] = await Promise.all([
        shopApi.catalog(),
        shopApi.inventory(),
        shopApi.bundles(),
        trialApi.list(),
      ]);
      setItems(items.filter((it) => (
        it.slot === 'avatarFrame'
        || it.slot === 'endgameFx'
        || it.slot === 'matchBackground'
      )));
      setBundles(bundleItems);
      setOwnedIds(new Set(
        owned
          .filter((cosmetic) => cosmetic.trialUsesLeft == null)
          .map((cosmetic) => cosmetic.id),
      ));
      setTrialsByItem(new Map(trialResult.trials.map((trial) => [trial.itemId, trial])));
    } catch (e) {
      setToast({ text: String(e), kind: 'err' });
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const buy = async (item: CosmeticItem) => {
    if (busyId) return;
    setBusyId(item.id);
    try {
      const r = await shopApi.buy(item.id);
      audio.play('ui.shop_purchase');
      setOwnedIds((s) => new Set(s).add(item.id));
      setProfile({ coins: r.coins, gems: r.gems });
      setToast({ text: t('shop.acquired', { name: item.name }), kind: 'ok' });
      // Refresh full profile (mmr etc. unchanged but keeps cache fresh)
      if (token) {
        void authApi.me(token).then(({ user }) => setUser(user));
      }
    } catch (e) {
      if (e instanceof ShopApiError) setToast({ text: e.message || e.code, kind: 'err' });
      else setToast({ text: t('common.network_error'), kind: 'err' });
    } finally {
      setBusyId(null);
      window.setTimeout(() => setToast(null), 2400);
    }
  };

  const buyBundle = async (bundle: ShopBundle) => {
    if (busyId) return;
    setBusyId(bundle.id);
    try {
      const result = await shopApi.buyBundle(bundle.id);
      audio.play('ui.shop_purchase');
      setBundles((current) =>
        current?.map((item) => (
          item.id === bundle.id ? { ...item, owned: true } : item
        )) ?? null,
      );
      setProfile({ coins: result.coins, gems: result.gems });
      setToast({ text: t('shop.combo_acquired', { name: bundle.name }), kind: 'ok' });
      if (token) {
        void authApi.me(token).then(({ user }) => setUser(user));
      }
    } catch (e) {
      if (e instanceof ShopApiError) setToast({ text: e.message || e.code, kind: 'err' });
      else setToast({ text: t('common.network_error'), kind: 'err' });
    } finally {
      setBusyId(null);
      window.setTimeout(() => setToast(null), 2400);
    }
  };

  const startTrial = async (item: CosmeticItem) => {
    if (busyId) return;
    const kind: TrialKind | null = item.slot === 'endgameFx'
      ? 'finisher'
      : item.slot === 'matchBackground'
        ? 'background'
        : null;
    if (!kind) return;
    setBusyId(item.id);
    try {
      const earned = await showRewarded();
      if (!earned) return;
      const result = await trialApi.start(kind, item.id);
      audio.play('ui.shop_purchase');
      await load();
      setToast({
        text: t('shop.trial_started', { name: item.name, n: result.usesLeft }),
        kind: 'ok',
      });
      if (token) {
        void authApi.me(token).then(({ user }) => setUser(user));
      }
    } catch (error) {
      audio.play('ui.error');
      setToast({
        text: error instanceof Error ? error.message : t('common.network_error'),
        kind: 'err',
      });
    } finally {
      setBusyId(null);
      window.setTimeout(() => setToast(null), 2400);
    }
  };

  return (
    <div className="screen">
      <div className="hex-bg" />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 18px 8px',
        }}
      >
        <BackButton
          onClick={() => { const dest = returnTo ?? 'home'; setReturnTo(null); go(dest); }}
          label={returnTo === 'room' ? 'ROOM' : undefined}
        />
        <div style={{ display: 'flex', gap: 6 }}>
          <Pill icon="coin">{profile.coins}</Pill>
          <Pill icon="gem">{profile.gems}</Pill>
        </div>
      </div>

      <div
        style={{
          padding: '0 18px 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div className="display" style={{ fontSize: 26 }}>{t('shop.title')}</div>
        <button
          onClick={() => { audio.play('ui.tap'); go('inventory'); }}
          aria-label={t('tab.bag')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
            padding: '7px 12px',
            borderRadius: 99,
            background: 'rgba(34,211,238,0.12)',
            border: '1px solid var(--c-cyan)',
            color: 'var(--c-cyan)',
            fontFamily: 'var(--f-display)',
            fontSize: 12,
            letterSpacing: '0.06em',
            cursor: 'pointer',
          }}
        >
          <ShopBagIcon />
          {t('tab.bag')}
        </button>
      </div>

      <CategoryTabs
        value={category}
        onChange={setCategory}
        showCombos={(bundles?.length ?? 0) > 0}
      />

      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 18px 22px' }}>
        {category === 'gems' && <GemsShopSection />}
        {category === 'combos' && (
          <>
            <div style={{ fontSize: 11, color: 'var(--ink-muted)', marginBottom: 10 }}>
              {t('shop.combos_hint')}
            </div>
            {!bundles ? (
              <div style={{ color: 'var(--ink-muted)', fontSize: 12, padding: 20, textAlign: 'center' }}>
                {t('common.loading')}
              </div>
            ) : bundles.length === 0 ? (
              <div style={{ color: 'var(--ink-muted)', fontSize: 12, padding: 20, textAlign: 'center' }}>
                {t('shop.no_combos')}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {bundles.map((bundle) => (
                  <BundleCard
                    key={bundle.id}
                    bundle={bundle}
                    busy={busyId === bundle.id}
                    canAfford={
                      profile.coins >= bundle.priceCoins
                      && profile.gems >= bundle.priceGems
                    }
                    onBuy={() => buyBundle(bundle)}
                    onPreviewCharacter={() => {
                      audio.play('ui.tap');
                      setPreviewFinisher(null);
                      setPreviewCharacter(bundle.character);
                    }}
                    onPreviewFinisher={() => {
                      audio.play('ui.tap');
                      setPreviewCharacter(null);
                      setPreviewFinisher(bundle.finisher);
                    }}
                  />
                ))}
              </div>
            )}
          </>
        )}
        {category === 'heroes' && <CharactersShopSection />}
        {category === 'skills' && <SkillsShopSection />}
        {category === 'holds' && <HoldSkinsShopSection />}
        {(category === 'frames' || category === 'finishers' || category === 'backgrounds') && (() => {
          const slot = category === 'finishers'
            ? 'endgameFx'
            : category === 'backgrounds'
              ? 'matchBackground'
              : 'avatarFrame';
          const list = items?.filter((it) => it.slot === slot) ?? null;
          const hintKey = category === 'finishers'
            ? 'shop.finishers_hint'
            : category === 'backgrounds'
              ? 'shop.backgrounds_hint'
              : 'shop.frames_hint';
          const emptyKey = category === 'finishers'
            ? 'shop.no_finishers'
            : category === 'backgrounds'
              ? 'shop.no_backgrounds'
              : 'shop.no_frames';
          return (
            <>
              <div style={{ fontSize: 11, color: 'var(--ink-muted)', marginBottom: 10 }}>
                {t(hintKey)}
              </div>
              {!list ? (
                <div style={{ color: 'var(--ink-muted)', fontSize: 12, padding: 20, textAlign: 'center' }}>
                  {t('common.loading')}
                </div>
              ) : list.length === 0 ? (
                <div style={{ color: 'var(--ink-muted)', fontSize: 12, padding: 20, textAlign: 'center' }}>
                  {t(emptyKey)}
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                    gap: 8,
                  }}
                >
                  {list.map((it) => (
                    <ShopCard
                      key={it.id}
                      item={it}
                      owned={ownedIds.has(it.id)}
                      trialUsesLeft={trialsByItem.get(it.id)?.usesLeft ?? null}
                      canTrial={
                        adsAvailable()
                        && !trialsByItem.has(it.id)
                        && (it.slot === 'endgameFx' || it.slot === 'matchBackground')
                        && (it.priceCoins > 0 || it.priceGems > 0)
                      }
                      busy={busyId === it.id}
                      canAffordCoins={profile.coins >= it.priceCoins}
                      canAffordGems={profile.gems >= it.priceGems}
                      onBuy={() => buy(it)}
                      onTrial={() => void startTrial(it)}
                      onPreview={
                        it.slot === 'endgameFx'
                          ? () => {
                              audio.play('ui.tap');
                              setPreviewCharacter(null);
                              setPreviewBackground(null);
                              setPreviewFinisher(it);
                            }
                          : it.slot === 'matchBackground'
                            ? () => {
                                audio.play('ui.tap');
                                setPreviewCharacter(null);
                                setPreviewFinisher(null);
                                setPreviewBackground(it);
                              }
                          : undefined
                      }
                    />
                  ))}
                </div>
              )}
            </>
          );
        })()}
      </div>

      {previewCharacter && (
        <PreviewDialog
          title={`${previewCharacter.name} · ${t('chars.idle_preview')}`}
          onClose={() => setPreviewCharacter(null)}
        >
          <CharacterAnimationPreview character={previewCharacter} />
        </PreviewDialog>
      )}

      {previewFinisher && (
        <PreviewDialog
          title={`${previewFinisher.name} · ${t('shop.finisher_preview')}`}
          onClose={() => setPreviewFinisher(null)}
        >
          <FinisherVideoPreview item={previewFinisher} />
        </PreviewDialog>
      )}

      {previewBackground && (
        <PreviewDialog
          title={`${previewBackground.name} · ${t('shop.background_preview')}`}
          onClose={() => setPreviewBackground(null)}
        >
          <MatchBackgroundPreview item={previewBackground} />
        </PreviewDialog>
      )}

      {toast && (
        <div
          style={{
            position: 'absolute',
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 22px)',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '10px 16px',
            borderRadius: 10,
            background:
              toast.kind === 'ok' ? 'rgba(34,211,238,0.18)' : 'rgba(230,57,70,0.18)',
            border: `1px solid ${toast.kind === 'ok' ? 'rgba(34,211,238,0.6)' : 'rgba(230,57,70,0.6)'}`,
            color: toast.kind === 'ok' ? 'var(--c-cyan)' : 'var(--c-red)',
            fontSize: 12,
            zIndex: 30,
            backdropFilter: 'blur(6px)',
          }}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}

function MatchBackgroundPreview({ item }: { item: CosmeticItem }) {
  const rawImage = (item.data.imageUrl as string | undefined) ?? item.iconUrl;
  const image = resolveAssetUrl(rawImage);
  const background = (item.data.background as string | undefined)
    ?? 'linear-gradient(180deg, #1E1B4B, #050314)';
  const accent = (item.data.accent as string | undefined) ?? '#22D3EE';
  return (
    <div
      style={{
        width: '100%',
        aspectRatio: '9 / 16',
        maxHeight: '68vh',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 16,
        border: `1px solid ${accent}99`,
        background: image
          ? `linear-gradient(rgba(5,3,20,.14), rgba(5,3,20,.45)), url("${image}") center / cover no-repeat`
          : background,
      }}
    >
      <div style={{ position: 'absolute', inset: '12% 10% 8%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {[0, 1].map((lane) => (
          <div key={lane} style={{ position: 'relative', borderRadius: 10, border: `1px solid ${accent}55`, background: 'rgba(5,3,20,.3)' }}>
            {['#E63946', '#3B82F6', '#F4C724', '#9B5DE5'].map((color, index) => (
              <span key={color} style={{ position: 'absolute', left: `${30 + (index % 2) * 38}%`, bottom: `${14 + index * 19}%`, width: 18, height: 18, borderRadius: '50%', background: color, boxShadow: `0 0 10px ${color}` }} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function CategoryTabs({
  value,
  onChange,
  showCombos,
}: {
  value: Category;
  onChange: (c: Category) => void;
  showCombos: boolean;
}) {
  const t = useT();
  return (
    <div className="shop-category-tabs">
      {CATEGORIES.filter((c) => c.id !== 'combos' || showCombos).map((c) => {
        const active = c.id === value;
        return (
          <button
            key={c.id}
            onClick={() => {
              audio.play('ui.tap');
              onChange(c.id);
            }}
            className="shop-category-tab"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              padding: '10px 10px',
              borderRadius: 12,
              cursor: 'pointer',
              scrollSnapAlign: 'center',
              background: active ? `color-mix(in srgb, ${c.accent} 16%, transparent)` : 'rgba(255,255,255,0.04)',
              border: active ? `1px solid ${c.accent}` : '1px solid var(--hairline)',
              color: active ? c.accent : 'var(--ink-muted)',
              transition: 'background 0.15s, border-color 0.15s',
            }}
          >
            <span style={{ fontSize: 18, lineHeight: 1 }}>{c.icon}</span>
            <span style={{ fontFamily: 'var(--f-display)', fontSize: 11, letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
              {t(c.labelKey).toUpperCase()}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function BundleCard({
  bundle,
  busy,
  canAfford,
  onBuy,
  onPreviewCharacter,
  onPreviewFinisher,
}: {
  bundle: ShopBundle;
  busy: boolean;
  canAfford: boolean;
  onBuy: () => void;
  onPreviewCharacter: () => void;
  onPreviewFinisher: () => void;
}) {
  const t = useT();
  const accent = RARITY_COLOR[bundle.rarity];
  const characterUrl = resolveAssetUrl(bundle.character.idleUrl);
  const frameUrl = resolveAssetUrl(bundle.avatarFrame.iconUrl);
  const finisherUrl = resolveAssetUrl(bundle.finisher.iconUrl);

  return (
    <div
      className="card"
      style={{
        padding: 12,
        borderColor: `${accent}66`,
        background: `linear-gradient(135deg, ${accent}18, rgba(8,5,28,0.92) 55%)`,
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
        <div
          style={{
            width: 102,
            minHeight: 140,
            flexShrink: 0,
            position: 'relative',
            borderRadius: 12,
            overflow: 'hidden',
            background: 'rgba(0,0,0,0.32)',
            border: `1px solid ${accent}55`,
          }}
        >
          {characterUrl && (
            <img
              src={characterUrl}
              alt={bundle.character.name}
              draggable={false}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          )}
          {frameUrl && (
            <img
              src={frameUrl}
              alt=""
              draggable={false}
              style={{
                position: 'absolute',
                inset: 4,
                width: 'calc(100% - 8px)',
                height: 'calc(100% - 8px)',
                objectFit: 'contain',
                pointerEvents: 'none',
              }}
            />
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
            <span className="display" style={{ fontSize: 17, color: accent }}>
              {bundle.name}
            </span>
            <span
              className="tag"
              style={{
                color: accent,
                border: `1px solid ${accent}77`,
                borderRadius: 4,
                padding: '2px 5px',
                fontSize: 8,
              }}
            >
              {bundle.rarity.toUpperCase()}
            </span>
          </div>
          {bundle.description && (
            <div style={{ color: 'var(--ink-muted)', fontSize: 10, marginTop: 4 }}>
              {bundle.description}
            </div>
          )}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '32px 1fr',
              gap: '5px 7px',
              alignItems: 'center',
              marginTop: 9,
              fontSize: 10,
            }}
          >
            <MiniAsset src={characterUrl} fallback="🧗" />
            <span>{bundle.character.name}</span>
            <MiniAsset src={frameUrl} fallback="◈" />
            <span>{bundle.avatarFrame.name}</span>
            <MiniAsset src={finisherUrl} fallback="💥" />
            <span>
              {bundle.finisher.name} · {Number(bundle.finisher.data.durationSec) || 6}s
            </span>
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 8,
          marginTop: 11,
        }}
      >
        <BundlePreviewButton
          icon="🧗"
          label={t('shop.cat_heroes')}
          accent="var(--c-cyan)"
          onClick={onPreviewCharacter}
        />
        <BundlePreviewButton
          icon="▶"
          label={t('shop.cat_finishers')}
          accent="var(--c-purple)"
          onClick={onPreviewFinisher}
        />
      </div>

      <button
        onClick={onBuy}
        disabled={bundle.owned || busy || !canAfford}
        style={{
          width: '100%',
          marginTop: 10,
          padding: '9px 10px',
          borderRadius: 9,
          border: `1px solid ${bundle.owned ? 'var(--c-cyan)' : accent}`,
          background: bundle.owned
            ? 'rgba(34,211,238,0.12)'
            : canAfford
              ? `linear-gradient(180deg, ${accent}, color-mix(in srgb, ${accent} 68%, #000))`
              : 'rgba(255,255,255,0.06)',
          color: bundle.owned
            ? 'var(--c-cyan)'
            : canAfford
              ? '#171126'
              : 'var(--ink-dim)',
          fontFamily: 'var(--f-display)',
          fontSize: 12,
          letterSpacing: '0.06em',
        }}
      >
        {bundle.owned
          ? `✓ ${t('shop.combo_owned')}`
          : busy
            ? '…'
            : bundle.priceGems > 0
              ? `${bundle.priceGems} 💎 · ${t('shop.combo_contains')}`
              : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    {bundle.priceCoins.toLocaleString()} <CoinIcon size={13} /> · {t('shop.combo_contains')}
                  </span>
                )}
      </button>
    </div>
  );
}

function BundlePreviewButton({
  icon,
  label,
  accent,
  onClick,
}: {
  icon: string;
  label: string;
  accent: string;
  onClick: () => void;
}) {
  const t = useT();

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${t('common.preview')} ${label}`}
      style={{
        minHeight: 48,
        padding: '7px 9px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        borderRadius: 10,
        border: `1px solid color-mix(in srgb, ${accent} 72%, transparent)`,
        background: `linear-gradient(180deg, color-mix(in srgb, ${accent} 14%, transparent), rgba(0,0,0,0.16))`,
        color: accent,
        cursor: 'pointer',
        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.08), 0 0 14px color-mix(in srgb, ${accent} 10%, transparent)`,
      }}
    >
      <span aria-hidden style={{ fontSize: 17, lineHeight: 1 }}>{icon}</span>
      <span style={{ minWidth: 0, textAlign: 'left' }}>
        <span
          style={{
            display: 'block',
            color: 'var(--ink-muted)',
            fontSize: 8,
            lineHeight: 1,
            letterSpacing: '0.08em',
          }}
        >
          {t('common.preview').toUpperCase()}
        </span>
        <span
          className="display"
          style={{
            display: 'block',
            marginTop: 4,
            color: accent,
            fontSize: 10,
            lineHeight: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label.toUpperCase()}
        </span>
      </span>
    </button>
  );
}

function MiniAsset({ src, fallback }: { src: string | null; fallback: string }) {
  return (
    <div
      style={{
        width: 30,
        height: 30,
        borderRadius: 7,
        overflow: 'hidden',
        display: 'grid',
        placeItems: 'center',
        background: 'rgba(255,255,255,0.07)',
        border: '1px solid var(--hairline)',
      }}
    >
      {src
        ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        : fallback}
    </div>
  );
}

function ShopCard({
  item,
  owned,
  trialUsesLeft,
  canTrial,
  busy,
  canAffordCoins,
  canAffordGems,
  onBuy,
  onTrial,
  onPreview,
}: {
  item: CosmeticItem;
  owned: boolean;
  trialUsesLeft: number | null;
  canTrial: boolean;
  busy: boolean;
  canAffordCoins: boolean;
  canAffordGems: boolean;
  onBuy: () => void;
  onTrial: () => void;
  onPreview?: () => void;
}) {
  const t = useT();
  const free = item.priceCoins === 0 && item.priceGems === 0;
  const canAfford = canAffordCoins && canAffordGems;
  const disabled = owned || busy || (!free && !canAfford);

  return (
    <div
      className="card"
      style={{
        padding: 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        position: 'relative',
        borderColor: `${RARITY_COLOR[item.rarity]}55`,
      }}
    >
      <span
        className="tag"
        style={{
          position: 'absolute',
          top: 6,
          right: 8,
          color: RARITY_COLOR[item.rarity],
          fontSize: 8,
        }}
      >
        {item.rarity.toUpperCase()}
      </span>
      {onPreview ? (
        <button
          type="button"
          onClick={onPreview}
          aria-label={`${t('common.preview')} ${item.name}`}
          style={{
            width: 68,
            height: 68,
            padding: 6,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 12,
            border: `1px solid ${RARITY_COLOR[item.rarity]}88`,
            background: 'rgba(0,0,0,0.28)',
            cursor: 'zoom-in',
            boxShadow: `0 0 14px ${RARITY_COLOR[item.rarity]}22`,
          }}
        >
          <CosmeticPreview item={item} size={56} />
        </button>
      ) : (
        <CosmeticPreview item={item} size={56} />
      )}
      <div
        style={{
          fontSize: 11,
          textAlign: 'center',
          fontWeight: 700,
          minHeight: 26,
        }}
      >
        {item.name}
      </div>
      {!owned && (trialUsesLeft ?? 0) > 0 && (
        <div
          className="display"
          style={{
            width: '100%',
            padding: '5px 6px',
            borderRadius: 7,
            textAlign: 'center',
            fontSize: 9,
            color: 'var(--c-cyan)',
            background: 'rgba(34,211,238,.12)',
            border: '1px dashed rgba(34,211,238,.55)',
          }}
        >
          📺 {t('inv.trial_left', { n: trialUsesLeft ?? 0 })} · {t('inv.equipped')}
        </div>
      )}
      {!owned && canTrial && (
        <button
          type="button"
          onClick={onTrial}
          disabled={busy}
          style={{
            width: '100%',
            padding: '7px 8px',
            borderRadius: 8,
            border: '1px dashed var(--c-cyan)',
            background: 'rgba(34,211,238,.12)',
            color: 'var(--c-cyan)',
            fontFamily: 'var(--f-display)',
            fontSize: 10,
            letterSpacing: '.05em',
          }}
        >
          {busy ? '…' : `📺 ${t('shop.trial')} ×2`}
        </button>
      )}
      <button
        onClick={onBuy}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '6px 8px',
          fontSize: 11,
          fontFamily: 'var(--f-display)',
          letterSpacing: '0.06em',
          border: 'none',
          borderRadius: 8,
          background: owned
            ? 'rgba(34,211,238,0.14)'
            : free
              ? 'rgba(34,211,238,0.2)'
              : canAfford
                ? 'linear-gradient(180deg, var(--c-yellow), #d9a800)'
                : 'rgba(255,255,255,0.06)',
          color: owned
            ? 'var(--c-cyan)'
            : !canAfford && !free
              ? 'var(--ink-dim)'
              : free
                ? 'var(--c-cyan)'
                : '#1e1b4b',
          cursor: disabled ? 'not-allowed' : 'pointer',
          boxShadow:
            owned || !canAfford || free
              ? 'none'
              : 'inset 0 1px 0 rgba(255,255,255,0.4), 0 3px 0 #8a6a00',
        }}
      >
        {owned
          ? `✓ ${t('shop.owned_tag')}`
          : busy
            ? '…'
            : free
              ? t('shop.claim')
              : item.priceGems > 0
                ? `${item.priceGems} 💎`
                : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      {item.priceCoins.toLocaleString()} <CoinIcon size={13} />
                    </span>
                  )}
      </button>
    </div>
  );
}

function FinisherVideoPreview({ item }: { item: FinisherPreviewSource }) {
  const rawMediaUrl =
    typeof item.data.mediaUrl === 'string' ? item.data.mediaUrl : null;
  const mediaUrl =
    getPreloadedMedia(rawMediaUrl)
    ?? (rawMediaUrl ? absoluteAssetUrl(rawMediaUrl) : null);
  const iconUrl = resolveAssetUrl(item.iconUrl);
  const durationSec = Number(item.data.durationSec) || 6;

  return (
    <>
      <div
        style={{
          width: '100%',
          aspectRatio: '9 / 16',
          maxHeight: '65vh',
          overflow: 'hidden',
          display: 'grid',
          placeItems: 'center',
          borderRadius: 14,
          border: '1px solid rgba(155,93,229,0.5)',
          background: '#000',
          boxShadow: 'inset 0 0 30px rgba(155,93,229,0.16)',
        }}
      >
        {mediaUrl ? (
          <video
            key={mediaUrl}
            src={mediaUrl}
            autoPlay
            loop
            controls
            playsInline
            preload="auto"
            style={{
              width: '100%',
              height: item.data.cutWatermark ? '108.7%' : '100%',
              objectFit: 'contain',
              objectPosition: 'center top',
              background: '#000',
            }}
          />
        ) : (
          <div style={{ textAlign: 'center', color: 'var(--ink-muted)' }}>
            {iconUrl ? (
              <img
                src={iconUrl}
                alt={item.name}
                draggable={false}
                style={{ width: 180, height: 180, objectFit: 'contain' }}
              />
            ) : (
              <div style={{ fontSize: 100, lineHeight: 1 }}>💥</div>
            )}
            <div style={{ marginTop: 12, fontSize: 11 }}>No video preview uploaded.</div>
          </div>
        )}
      </div>
      <div
        className="mono"
        style={{
          marginTop: 10,
          color: 'var(--ink-muted)',
          fontSize: 10,
          textAlign: 'center',
        }}
      >
        {durationSec.toFixed(2)}s · {(item.data.keyType as string | undefined) ?? 'green'} key
      </div>
    </>
  );
}

function ShopBagIcon() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6.5 8.5a5.5 5.5 0 0 1 11 0V19a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1z" />
      <path d="M9.5 8.5a2.5 2.5 0 0 1 5 0" />
      <rect x="9.5" y="12.5" width="5" height="4.5" rx="1" />
    </svg>
  );
}
